import {
  endOfDay,
  endOfMonth,
  format,
  getTime,
  isSameMonth,
  isSameYear,
  startOfDay,
  startOfMonth,
  subMonths,
  UTCDate,
} from '@/utils/dates';
import { Column } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { exhaustiveCheck } from '../../utils/exhaustive-check';
import { Button } from '../button/button';
import { Calendar } from '../calendar/calendar';
import { Inline } from '../inline/inline';
import { Popover } from '../popover/popover';
import { Stack } from '../stack/stack';

type Preset = 'PREVIOUS_MONTH' | 'CURRENT_MONTH' | 'PREVIOUS_THREE_MONTHS' | 'PREVIOUS_SIX_MONTHS' | 'ALL_TIME';

type DateRangeFilterProps<TData> = {
  column?: Column<TData>;
};

export const DateRangeFilter = <TData,>({ column }: DateRangeFilterProps<TData>) => {
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>();
  const [month, setMonth] = useState<Date | undefined>();
  const [isOpen, setIsOpen] = useState(false);
  const [triggerText, setTriggerText] = useState<string>('Date');

  // Sync local state with column filter value (set from URL params on mount)
  useEffect(() => {
    const filterValue = column?.getFilterValue() as [number, number] | undefined;

    if (!filterValue || !Array.isArray(filterValue) || filterValue.length !== 2) {
      return;
    }

    const [startTimestamp, endTimestamp] = filterValue;
    const startDate = new UTCDate(startTimestamp);
    const endDate = new UTCDate(endTimestamp);
    const range = { from: startDate, to: endDate };

    setSelectedRange(range);
    setTriggerText(getTriggerText(range));
    setMonth(startDate);
  }, [column]);

  if (
    !column ||
    // Filter should not display if there are no values in the column
    !column.getFacetedUniqueValues().size
  ) {
    return null;
  }

  const minMaxValues = column.getFacetedMinMaxValues();
  const earliestDate = minMaxValues ? new UTCDate(minMaxValues[0]) : undefined;

  const onSelectRange = () => {
    if (selectedRange?.from && selectedRange?.to) {
      const fromDate = getTime(startOfDay(selectedRange.from));
      const endOfDayToDate = getTime(endOfDay(selectedRange.to));
      const selectedTriggerText = getTriggerText(selectedRange);

      setTriggerText(selectedTriggerText);
      column?.setFilterValue([fromDate, endOfDayToDate]);
      setIsOpen(false);

      return;
    }

    setTriggerText('Date');
    column?.setFilterValue(undefined);
    setIsOpen(false);
  };

  const onClearRange = () => {
    setSelectedRange(undefined);
    setTriggerText('Date');
    column?.setFilterValue(undefined);
    setIsOpen(false);
  };

  const onPresetClick = (preset: Preset) => {
    const range = getDateRange(preset, earliestDate);

    if (!range) {
      return;
    }

    setMonth(range.from);
    setSelectedRange(range);
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      triggerElement={
        <Button
          variant="secondary"
          className="group"
          iconRight={<ChevronDown className="h-4 w-4" />}
          iconRightClassName="transition-transform duration-200 ease-out group-data-[state=open]:rotate-180"
        >
          {triggerText}
        </Button>
      }
      content={
        <Stack align="stretch">
          <Inline align="stretch">
            {/* We control the month so we have range presets */}
            <Calendar
              mode="range"
              selected={selectedRange}
              onSelect={setSelectedRange}
              month={month}
              onMonthChange={setMonth}
              showOutsideDays={false}
              className="p-5"
            />

            <Stack justify="stretch" space={0.5} className="border-l-[0.5px] border-border p-5">
              <DateRangePreset label="This month" onSelect={() => onPresetClick('CURRENT_MONTH')} />

              <DateRangePreset label="Last month" onSelect={() => onPresetClick('PREVIOUS_MONTH')} />

              <DateRangePreset label="Last 3 months" onSelect={() => onPresetClick('PREVIOUS_THREE_MONTHS')} />

              <DateRangePreset label="Last 6 months" onSelect={() => onPresetClick('PREVIOUS_SIX_MONTHS')} />

              {earliestDate && <DateRangePreset label="All time" onSelect={() => onPresetClick('ALL_TIME')} />}
            </Stack>
          </Inline>

          <Inline justify="end" space={3} className="border-t-[0.5px] border-border px-5 py-4">
            <Button variant="secondary" onClick={onClearRange}>
              Clear
            </Button>

            <Button onClick={onSelectRange}>Apply</Button>
          </Inline>
        </Stack>
      }
      side="bottom"
      align="start"
    />
  );
};

const getDateRange = (preset: Preset, earliestDate?: Date): DateRange | null => {
  const today = new UTCDate();

  switch (preset) {
    case 'PREVIOUS_MONTH': {
      const lastMonth = subMonths(today, 1);

      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    case 'CURRENT_MONTH':
      return { from: startOfMonth(today), to: today };
    case 'PREVIOUS_THREE_MONTHS': {
      const threeMonthsAgo = subMonths(today, 3);

      return { from: threeMonthsAgo, to: today };
    }
    case 'PREVIOUS_SIX_MONTHS': {
      const sixMonthsAgo = subMonths(today, 6);

      return { from: sixMonthsAgo, to: today };
    }
    case 'ALL_TIME': {
      if (!earliestDate) {
        return null;
      }

      const earliest = new UTCDate(earliestDate);

      return { from: earliest, to: today };
    }
    default:
      return exhaustiveCheck(preset);
  }
};

/**
 * Formats a date range with smart abbreviation based on the range:
 * - Same month: `5 – 15 Nov 2025`
 * - Different month, same year: `28 Oct – 4 Nov 2025`
 * - Cross year: `28 Dec 2025 – 4 Jan 2026`
 */
const getTriggerText = (selectedRange?: DateRange) => {
  if (!selectedRange?.from || !selectedRange?.to) {
    return 'Date';
  }

  const { from, to } = selectedRange;

  if (isSameMonth(from, to)) {
    // Same month: 5 – 15 Nov 2025
    const fromDay = format(from, 'd');
    const toFormatted = format(to, 'd MMM yyyy');
    return `${fromDay} – ${toFormatted}`;
  }

  if (isSameYear(from, to)) {
    // Different month, same year: 28 Oct – 4 Nov 2025
    const fromFormatted = format(from, 'd MMM');
    const toFormatted = format(to, 'd MMM yyyy');
    return `${fromFormatted} – ${toFormatted}`;
  }

  // Cross year: 28 Dec 2025 – 4 Jan 2026
  const fromFormatted = format(from, 'd MMM yyyy');
  const toFormatted = format(to, 'd MMM yyyy');
  return `${fromFormatted} – ${toFormatted}`;
};

type DateRangePresetProps = {
  label: string;
  onSelect: () => void;
};

const DateRangePreset = ({ label, onSelect }: DateRangePresetProps) => {
  return (
    <Button variant="ghost" className="w-full rounded-lg bg-white hover:bg-accent" onClick={onSelect}>
      <p className="w-full text-left text-xs leading-5 font-normal tracking-[-0.24px] text-black">{label}</p>
    </Button>
  );
};
